import { type DragEvent, memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { type WorkspaceSidebarContainerProps } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarContainer';

export type PushModeContainerProps = Pick<
  WorkspaceSidebarContainerProps,
  | 'suppressHoverOpen'
  | 'onNotificationsHover'
  | 'onHoverZoneLeave'
  | 'onPushPointerLeave'
  | 'onPushFocus'
  | 'onPushBlur'
  | 'children'
> & {
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
};

export const PushModeContainer = memo(
  ({
    suppressHoverOpen,
    onNotificationsHover,
    onHoverZoneLeave,
    onPushPointerLeave,
    onDragLeave,
    onPushFocus,
    onPushBlur,
    children,
  }: PushModeContainerProps) => {
    return (
      <>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Invisible hover trigger zone for push-mode sidebar. Keyboard accessibility handled by onFocus/onBlur on the sidebar section below. */}
        <div
          className={cn('absolute inset-y-0 left-0 z-50 w-4', !suppressHoverOpen && 'cursor-default')}
          onPointerEnter={onNotificationsHover}
          onPointerLeave={onHoverZoneLeave}
          onDragEnter={onNotificationsHover}
        />
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Push-mode sidebar panel — pointer/drag/focus handlers control overlay visibility. Keyboard access provided via onFocus/onBlur bubbling from focusable children. */}
        <section
          className="absolute inset-y-0 left-0 z-50 w-[var(--sidebar-width)] -translate-x-full bg-grey-4 transition-transform duration-200 ease-out group-data-[sidebar-overlay=open]/layout:translate-x-0"
          data-workspace-sidebar-overlay
          onPointerEnter={onNotificationsHover}
          onPointerLeave={onPushPointerLeave}
          onDragLeave={onDragLeave}
          onFocus={onPushFocus}
          onBlur={onPushBlur}
        >
          <div className="flex h-full min-h-0 flex-col">{children}</div>
        </section>
      </>
    );
  },
);

PushModeContainer.displayName = 'PushModeContainer';
